<template>
  <v-tooltip
    v-model="show"
    :right="right"
    :bottom="bottom"
    open-on-click
    :open-on-hover="!isMobile"
  >
    <template #activator="{ on, attrs }">
      <span
        v-bind="attrs"
        v-on="on"
        @click.stop="handleClick"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
        @touchcancel="handleTouchCancel"
        @contextmenu.prevent
      >
        <slot />
      </span>
    </template>
    <span>{{ tooltipText }}</span>
  </v-tooltip>
</template>

<script>
// Global tooltip manager to handle closing other tooltips
const tooltipManager = {
  activeTooltips: new Set(),
  
  register(tooltip) {
    this.activeTooltips.add(tooltip);
  },
  
  unregister(tooltip) {
    this.activeTooltips.delete(tooltip);
  },
  
  closeOthers(currentTooltip) {
    this.activeTooltips.forEach(tooltip => {
      if (tooltip !== currentTooltip) {
        tooltip.closeTooltip();
      }
    });
  }
};
export default {
  name: 'BaseTooltip',
  props: {
    tooltipText: {
      type: String,
      default: '',
    },
    right: {
      type: Boolean,
      default: false
    },
    bottom: {
      type: Boolean,
      default: false
    },
    longPressDuration: {
      type: Number,
      default: 500
    }
  },
  data() {
    return {
      show: false,
      longPressTimer: null,
      touchStartTime: 0,
      isLongPress: false,
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    }
  },
  created() {
    // Register this tooltip instance with the global manager
    tooltipManager.register(this);
  },
  beforeDestroy() {
    // Unregister this tooltip instance and clean up timer
    tooltipManager.unregister(this);
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }
  },
  methods: {
    handleClick() {
      if (this.isMobile) {
        // If it was a long press, don't handle the click
        if (this.isLongPress) {
          this.isLongPress = false;
          return;
        }
        
        // Regular click behavior for mobile
        this.toggleTooltip();
      }
    },
    handleTouchStart() {
      if (!this.isMobile) return;
      this.touchStartTime = Date.now();
      this.isLongPress = false;
      
      // Start long press timer
      this.longPressTimer = setTimeout(() => {
        this.isLongPress = true;
        this.showTooltipOnLongPress();
      }, this.longPressDuration);
    },
    
    handleTouchEnd() {
      if (!this.isMobile) return;
      // Clear the long press timer
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      
      // If it wasn't a long press and tooltip is showing, allow normal click behavior
      const touchDuration = Date.now() - this.touchStartTime;
      if (touchDuration < this.longPressDuration && !this.show) {
        // This will be handled by handleClick
        return;
      }
    },
    handleTouchCancel() {
      if (!this.isMobile) return;
      // Clear the long press timer
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.isLongPress = false;
    },
    showTooltipOnLongPress() {
      // Close other tooltips first
      tooltipManager.closeOthers(this);
      this.show = true;
      // Auto-hide after a delay
      setTimeout(() => {
        this.show = false;
      }, 2000);
    },
    toggleTooltip() {
      if (this.isMobile) {
        // If opening the tooltip, close all others first
        if (!this.show) {
          tooltipManager.closeOthers(this);
        }
        this.show = !this.show;
      }
    },
    closeTooltip() {
      this.show = false;
    }
  }
};
</script>

<style scoped>
/* Ensure tooltip is visible and has proper z-index */
:deep(.v-tooltip__content) {
  z-index: 1000 !important;
  opacity: 1 !important;
}
</style>
